import { PROBLEM_CODES, errorMessage, invalidField, isProblemError, problemCode } from "../../api";
import type { SignupRequestBody } from "../../api";

/**
 * La logique pure du formulaire d'inscription : normalisation, validation
 * côté client, traduction vers le corps du contrat, et mise en mots des refus
 * du serveur. Sans React, donc testée.
 *
 * Les règles reproduisent celles du serveur (`account.NewSignupRequest`) :
 * un refus côté client épargne un aller-retour, il n'en remplace aucun.
 */
export type ChampInscription =
  | "entreprise"
  | "contact"
  | "fonction"
  | "email"
  | "telephone"
  | "siret"
  | "site"
  | "besoin";

export type InscriptionSaisie = Record<ChampInscription, string>;

export const SAISIE_VIDE: InscriptionSaisie = {
  entreprise: "",
  contact: "",
  fonction: "",
  email: "",
  telephone: "",
  siret: "",
  site: "",
  besoin: "",
};

/** Bornes de longueur, alignées sur le domaine. */
export const BORNES: Record<ChampInscription, number> = {
  entreprise: 120,
  contact: 120,
  fonction: 120,
  email: 254,
  telephone: 32,
  siret: 14,
  site: 254,
  besoin: 2000,
};

const OBLIGATOIRES: ChampInscription[] = ["entreprise", "contact", "fonction", "email", "telephone", "besoin"];

/** Libellés des champs, pour les messages. */
export const LIBELLES: Record<ChampInscription, string> = {
  entreprise: "Nom de l'entreprise",
  contact: "Nom du contact",
  fonction: "Fonction du contact",
  email: "Adresse e-mail professionnelle",
  telephone: "Téléphone",
  siret: "SIRET",
  site: "Site web",
  besoin: "Votre besoin",
};

/**
 * Normalise ce qu'on a tapé, comme le serveur le fera : espaces autour
 * retirés, SIRET sans espaces, site préfixé de `https://` s'il n'a pas de
 * schéma. Le besoin garde ses retours à la ligne.
 */
export function normaliserInscription(s: InscriptionSaisie): InscriptionSaisie {
  const site = s.site.trim();
  return {
    entreprise: s.entreprise.trim(),
    contact: s.contact.trim(),
    fonction: s.fonction.trim(),
    email: s.email.trim().toLowerCase(),
    telephone: s.telephone.trim(),
    siret: s.siret.replace(/\s+/g, ""),
    site: site === "" || site.includes("://") ? site : `https://${site}`,
    besoin: s.besoin.trim(),
  };
}

/** Quatorze chiffres exactement. Pas de clé de Luhn : même règle que le serveur. */
export function siretValide(s: string): boolean {
  return /^\d{14}$/.test(s);
}

/** Une URL http(s) absolue dont l'hôte porte un point. */
export function urlValide(s: string): boolean {
  try {
    const u = new URL(s);
    return (u.protocol === "http:" || u.protocol === "https:") && u.hostname.includes(".");
  } catch {
    return false;
  }
}

/** Un arobase, quelque chose de part et d'autre, un point dans le domaine. */
export function emailPlausible(s: string): boolean {
  const [local, domaine, ...reste] = s.split("@");
  return reste.length === 0 && !!local && !!domaine && domaine.includes(".") && !/\s/.test(s);
}

/** Entre 8 et 15 chiffres, une fois retirés `+`, espaces, points, tirets et parenthèses. */
export function telephonePlausible(s: string): boolean {
  const chiffres = s.replace(/[\s+.\-()]/g, "");
  return /^\d{8,15}$/.test(chiffres);
}

/**
 * Valide une saisie **déjà normalisée**. Rend un message par champ refusé ;
 * un objet vide vaut « rien à redire ».
 */
export function validerInscription(s: InscriptionSaisie): Partial<Record<ChampInscription, string>> {
  const erreurs: Partial<Record<ChampInscription, string>> = {};
  for (const champ of OBLIGATOIRES) {
    if (s[champ] === "") {
      erreurs[champ] = "Ce champ est obligatoire.";
    }
  }
  for (const champ of Object.keys(BORNES) as ChampInscription[]) {
    if (erreurs[champ] === undefined && [...s[champ]].length > BORNES[champ]) {
      erreurs[champ] = `Au plus ${BORNES[champ]} caractères.`;
    }
  }
  if (erreurs.email === undefined && !emailPlausible(s.email)) {
    erreurs.email = "Cette adresse n'a pas une forme valide.";
  }
  if (erreurs.telephone === undefined && !telephonePlausible(s.telephone)) {
    erreurs.telephone = "Indiquez un numéro de 8 à 15 chiffres.";
  }
  if (s.siret !== "" && !siretValide(s.siret)) {
    erreurs.siret = "Le SIRET compte exactement 14 chiffres.";
  }
  if (s.site !== "" && erreurs.site === undefined && !urlValide(s.site)) {
    erreurs.site = "Indiquez l'adresse du site, par exemple exemple.fr.";
  }
  return erreurs;
}

/**
 * Le corps du contrat, depuis une saisie normalisée. Les facultatifs vides
 * sont omis, pas envoyés vides ; le pot de miel n'est envoyé que s'il a été
 * rempli — un humain ne le voit pas.
 */
export function versRequete(s: InscriptionSaisie, fax = ""): SignupRequestBody {
  const corps: SignupRequestBody = {
    company_name: s.entreprise,
    contact_name: s.contact,
    contact_title: s.fonction,
    email: s.email,
    phone: s.telephone,
    message: s.besoin,
  };
  if (s.siret !== "") {
    corps.siret = s.siret;
  }
  if (s.site !== "") {
    corps.website = s.site;
  }
  if (fax !== "") {
    corps.fax = fax;
  }
  return corps;
}

const CHAMP_PAR_NOM_SERVEUR: Record<string, ChampInscription> = {
  company_name: "entreprise",
  contact_name: "contact",
  contact_title: "fonction",
  email: "email",
  phone: "telephone",
  siret: "siret",
  website: "site",
  message: "besoin",
};

/** Le champ que le serveur refuse, s'il en nomme un. */
export function champRefuse(err: unknown): ChampInscription | null {
  const nom = invalidField(err);
  if (nom !== null) {
    return CHAMP_PAR_NOM_SERVEUR[nom] ?? null;
  }
  switch (problemCode(err)) {
    case PROBLEM_CODES.invalidEmail:
      return "email";
    case PROBLEM_CODES.invalidCompanyName:
      return "entreprise";
    default:
      return null;
  }
}

/**
 * Le texte à montrer quand le dépôt échoue. Le cas `email_taken` n'arrive
 * pas ici — le serveur rend 202 quoi qu'il en soit — mais un serveur plus
 * ancien pourrait le rendre : on le traite.
 */
export function messageInscription(err: unknown, restant: number): string {
  const delai = restant > 0 ? ` Réessayez dans ${restant} s.` : "";
  switch (problemCode(err)) {
    case PROBLEM_CODES.rateLimited:
      return `Trop de demandes depuis cet appareil.${delai}`;
    case PROBLEM_CODES.invalidSignup:
      return "Un champ est vide, trop long ou mal formé. Vérifiez le SIRET et l'adresse du site.";
    case PROBLEM_CODES.emailTaken:
      return "Un compte existe déjà pour cette adresse : connectez-vous.";
    case PROBLEM_CODES.csrfRejected:
      return "La requête a été refusée par le contrôle d'origine. Rechargez la page et réessayez.";
    default:
      return isProblemError(err) ? err.detail : errorMessage(err);
  }
}
